import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, MessageCircle, ChevronDown } from "lucide-react";
import { NAV } from "@/data/content";
import { COUNTRIES } from "@/data/content";
import { useSite } from "@/context/SiteContext";
import logo from "@/assets/logo.webp";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [countriesOpen, setCountriesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { openWhatsApp } = useSite();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setCountriesOpen(false); }, [location.pathname]);

  const isActive = (href) => location.pathname === href || (href !== "/" && location.pathname.startsWith(href));

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "glass-nav shadow-[0_4px_24px_-12px_rgba(11,25,44,0.25)]" : "bg-white"}`}
    >
      <div className="songket-band" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-20 flex items-center justify-between gap-4">
          <Link to="/" data-testid="nav-logo" className="flex items-center shrink-0">
            <img src={logo} alt="Global Pass Consultants" className="h-10 md:h-12 w-auto" />
          </Link>

          <nav className="hidden xl:flex items-center gap-1">
            {NAV.map((item) =>
              item.title === "Countries" ? (
                <div key={item.href} className="relative" onMouseEnter={() => setCountriesOpen(true)} onMouseLeave={() => setCountriesOpen(false)}>
                  <Link
                    to={item.href}
                    data-testid="nav-link-countries"
                    className={`px-3 py-2 text-sm font-semibold rounded-md flex items-center gap-1 whitespace-nowrap transition-colors ${isActive(item.href) ? "text-emerald-brand" : "text-navy hover:text-emerald-brand"}`}
                  >
                    {item.short || item.title} <ChevronDown size={14} />
                  </Link>
                  {countriesOpen && (
                    <div className="absolute top-full left-0 pt-2">
                      <div className="bg-white rounded-xl shadow-xl border border-slate-100 p-2 w-56">
                        {COUNTRIES.map((c) => (
                          <Link key={c.slug} to={`/countries/${c.slug}`} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-navy hover:bg-slate-50 font-medium">
                            <span>{c.flag}</span> {c.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.href}
                  to={item.href}
                  data-testid={`nav-link-${item.href.replace(/\//g, "") || "home"}`}
                  className={`px-3 py-2 text-sm font-semibold rounded-md whitespace-nowrap transition-colors relative ${item.highlight ? "text-navy" : "text-navy hover:text-emerald-brand"} ${isActive(item.href) ? "text-emerald-brand" : ""}`}
                >
                  {item.short || item.title}
                  {item.highlight && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-gold" />}
                </Link>
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <button
              data-testid="nav-cta-eligibility"
              onClick={() => navigate("/eligibility")}
              className="px-4 py-2.5 text-sm font-semibold rounded-full border-2 border-navy text-navy hover:bg-navy hover:text-white transition-colors"
            >
              Check Your Eligibility
            </button>
            <button
              data-testid="nav-cta-book-consultation"
              onClick={() => navigate("/contact")}
              className="px-4 py-2.5 text-sm font-bold rounded-full btn-gold"
            >
              Book a Consultation
            </button>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="xl:hidden p-2 text-navy"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-menu" className="xl:hidden bg-white border-t border-slate-100 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            {NAV.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`block px-4 py-3 rounded-lg font-semibold ${isActive(item.href) ? "bg-emerald-brand text-white" : "text-navy hover:bg-slate-50"}`}
              >
                {item.short || item.title} {item.highlight && <span className="text-gold">•</span>}
              </Link>
            ))}
            <div className="pt-3 flex flex-col gap-2">
              <button onClick={() => navigate("/eligibility")} className="w-full px-4 py-3 rounded-full border-2 border-navy text-navy font-semibold">Check Your Eligibility</button>
              <button onClick={() => navigate("/contact")} className="w-full px-4 py-3 rounded-full btn-gold font-bold">Book a Consultation</button>
              <button onClick={() => openWhatsApp({ service: "General Inquiry" })} className="w-full px-4 py-3 rounded-full bg-emerald-brand text-white font-semibold flex items-center justify-center gap-2">
                <MessageCircle size={18} /> Speak on WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
